
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { usePageSEO } from '../../hooks/usePageSEO';
import { createBreadcrumbStructuredData, createArticleStructuredData } from '../../utils/structuredData';

export default function FxBasicPage() {
  usePageSEO({
    title: 'FX基礎知識｜レバレッジ・スプレッド・経済指標を解説',
    description: 'FX取引に必要な基礎知識を解説。レバレッジの仕組み、スプレッドの比較、経済指標の見方など、取引に役立つ情報をまとめています。',
    keywords: 'FX,基礎知識,レバレッジ,スプレッド,経済指標,取引時間',
    structuredData: [
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: 'FX基礎知識', url: '/fx-basic' },
      ]),
      createArticleStructuredData({
        headline: 'FX基礎知識',
        description: 'FX取引に必要な基礎知識を解説。レバレッジ、スプレッド、経済指標など。',
        author: 'DMM FX情報局',
        datePublished: '2024-01-15',
        dateModified: new Date().toISOString().split('T')[0],
        image: 'https://public.readdy.ai/ai/img_res/25d8010a90a22ae3435ac966eb600490.jpg',
        url: '/fx-basic',
      }),
    ],
  });

  const articles = [
    {
      icon: 'ri-scales-3-line',
      title: 'レバレッジとは？',
      description: 'レバレッジの仕組み、最大25倍の活用方法、リスク管理を詳しく解説。',
      url: '/leverage',
    },
    {
      icon: 'ri-funds-line',
      title: 'スプレッドとは？',
      description: 'スプレッドの仕組みと他社比較。取引コストを抑える方法を解説。',
      url: '/spread',
    },
    {
      icon: 'ri-line-chart-line',
      title: '経済指標の見方',
      description: 'FX取引で重要な経済指標の活用方法を解説。',
      url: '/economic-indicators',
    },
    {
      icon: 'ri-time-line',
      title: '取引時間について',
      description: 'FXの取引時間、おすすめの時間帯を解説。',
      url: '/trading-hours',
    },
    {
      icon: 'ri-bank-card-line',
      title: '入出金方法',
      description: 'DMM FXの入金・出金方法を詳しく解説。',
      url: '/deposit-withdrawal',
    },
    {
      icon: 'ri-scales-line',
      title: '他社との比較',
      description: 'DMM FXと他社FX業者を徹底比較。',
      url: '/comparison',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'FX基礎知識', url: '/fx-basic' },
          ]} />

          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">FX基礎知識</h1>
            <p className="text-gray-600 leading-relaxed">
              FX取引に必要な基礎知識を解説。レバレッジ、スプレッド、経済指標など、取引に役立つ情報をまとめています。
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article, index) => (
              <Link
                key={index}
                to={article.url}
                className="group bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-amber-300 transition-all cursor-pointer"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${article.icon} text-white text-xl`}></i>
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                  {article.title}
                </h2>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">{article.description}</p>
                <div className="flex items-center text-amber-600 text-sm font-semibold">
                  <span>詳しく見る</span>
                  <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
                </div>
              </Link>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8">
            <p className="text-gray-700 mb-4">まずは無料で口座開設してみましょう</p>
            <a 
              href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
              rel="nofollow"
              className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:scale-105 transition-all whitespace-nowrap cursor-pointer shadow-lg"
            >
              無料でDMM FX口座を開設する
            </a>
            <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
